import { useEffect } from "react";
import Joyride, { CallBackProps, Placement, Step } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useTour } from "@/hooks/useTour";
import { ToolTipLayout } from "./ToolTipLayout";
import { useViewportWidth } from "@/hooks/useViewportWidth";

const CirclesPageTour = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { tourState, nextStep, previousStep, skipTour: skipTourFromContext } = useTour();
  const isMobile = useViewportWidth() < 768;

  // Only show tour if it's the circles-page type
  const shouldShowTour = tourState.isActive && tourState.tourType === "circles-page";
  const isShortTour = tourState.totalSteps === 3;

  // Get responsive placement based on step and device
  const getResponsivePlacement = (
    step: number,
    defaultPlacement: Placement | "center",
  ): Placement | "center" => {
    if (!isMobile) {
      return defaultPlacement;
    }

    if (step === 3) {
      return "bottom";
    }

    return "top";
  };

  // Get responsive styles based on step and device
  const getResponsiveStyles = (step: number) => {
    let maxWidth: string;
    if (isMobile) {
      maxWidth = "100%";
    } else {
      maxWidth = step === 0 ? "556px" : "428px";
    }

    let width: string;
    if (isMobile) {
      width = step === 0 ? "100vw" : "calc(100vw - 32px)";
    } else {
      width = "100%";
    }

    const baseStyles = {
      tooltip: {
        borderRadius: isMobile && step === 0 ? undefined : 16,
        fontSize: isMobile ? 14 : 16,
        fontFamily: "inherit",
        maxWidth,
        width,
        textAlign: "center" as const,
        overflow: "hidden",
      },
      tooltipContent: {
        padding: isMobile ? "24px 16px" : "32px",
      },
    };

    return baseStyles;
  };

  useEffect(() => {
    if (!tourState.isActive) {
      return;
    }

    // Wait for target elements to be available
    const waitForTargetElement = (retryCount = 0) => {
      const maxRetries = 20; // Max 2 seconds should be enough for regular elements
      const steps = getCurrentPageSteps();
      const stepIndex = getCurrentStepIndex();

      if (steps.length === 0 || stepIndex < 0 || stepIndex >= steps.length) {
        return;
      }

      const currentStep = steps[stepIndex];
      const targetElement = document.querySelector(currentStep.target as string);

      if (targetElement || currentStep.target === "body") {
        // Element found, tour will show automatically via shouldShowTour
        return;
      } else if (retryCount < maxRetries) {
        setTimeout(() => waitForTargetElement(retryCount + 1), 100);
      } else {
        // Element not found after retries, skip to next step
        nextStep();
      }
    };

    waitForTargetElement();
  }, [
    tourState.isActive,
    tourState.currentStep,
    location.pathname,
    location.search?.page,
    nextStep,
  ]);

  const getCurrentPageSteps = (): Step[] => {
    const pathname = location.pathname;
    const page = location.search?.page;

    if (pathname === "/coh") {
      return getLandingPageSteps();
    } else if (pathname === "/donor-profile" && page === "circles") {
      return getCirclesPageSteps();
    }

    return [];
  };

  const skipTour = () => {
    // If started from the landing page, skip to landing page
    // If started from circle page, skip to circles page
    skipTourFromContext();

    const pathname = location.pathname;
    if (pathname === "/coh") {
      navigate({ to: "/coh" });
    } else {
      navigate({ to: "/donor-profile", search: { page: "circles" } });
    }
  };

  const getLandingPageSteps = (): Step[] => [
    {
      target: "#joyride-join-efb",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={tourState.currentStep > 0}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.joinEfb.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.joinEfb.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(0, "left"),
      hideFooter: true,
      disableBeacon: true,
      styles: getResponsiveStyles(0),
    },
  ];

  const getCirclesPageSteps = (): Step[] => [
    // Step 1: Circles Cards
    {
      target: "#joyride-circle-cards",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.circlesCards.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.circlesCards.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(1, "top"),
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true,
      styles: getResponsiveStyles(1),
    },
    // Step 2: Set Default Circle
    {
      target: "#joyride-circle-card",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.setDefaultCircle.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.setDefaultCircle.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(2, "left-start"),
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true, // Allow clicks within the spotlight area
      styles: getResponsiveStyles(2),
    },
    // Step 3: Create Circle Button
    {
      target: "#joyride-create-circle",
      content: (
        <ToolTipLayout
          onClose={skipTour}
          currentStep={tourState.currentStep}
          totalSteps={tourState.totalSteps}
          onNext={nextStep}
          onPrevious={previousStep}
          showNavigation={true}
          showPrevious={true}
          showNext={true}
        >
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "650",
              marginBottom: "16px",
              color: "#232329",
              lineHeight: "1.2",
            }}
          >
            {t("tour.createCircle.title")}
          </h2>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "350",
              color: "#232329",
              lineHeight: "1.5",
              margin: "0 0 24px 0",
            }}
          >
            {t("tour.createCircle.description")}
          </p>
        </ToolTipLayout>
      ),
      placement: getResponsivePlacement(3, "left"),
      hideFooter: true,
      disableBeacon: true,
      disableOverlayClose: true,
      disableCloseOnEsc: true,
      spotlightClicks: true, // Allow clicks within the spotlight area
      styles: getResponsiveStyles(3),
    },
  ];

  const getCurrentStepIndex = (): number => {
    const pathname = location.pathname;
    let stepIndex = 0;

    if (pathname === "/coh") {
      // When starting from landing page, use the current step directly
      stepIndex = tourState.currentStep; // Step 0 for landing, then 1-3 for circles
    } else if (pathname === "/donor-profile" && location.search?.page === "circles") {
      // When starting from circles page, adjust for skipped landing step
      stepIndex = isShortTour ? tourState.currentStep : tourState.currentStep - 1; // Steps 0-2 for circles (since we skip landing step)
    }
    return stepIndex;
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action } = data;
    // Handle tour completion or skipping
    if (action === "skip" || action === "close") {
      skipTour(); // Mark as skipped with persistent tracking
      isShortTour
        ? navigate({ to: "/coh" }) // If short tour, go to landing page
        : navigate({ to: "/donor-profile", search: { page: "circles" } }); // Otherwise go to circles page
    }
  };

  const steps = getCurrentPageSteps();
  const stepIndex = getCurrentStepIndex();

  // More defensive checking - only show for create-circle tour
  if (!shouldShowTour || steps.length === 0 || stepIndex < 0 || stepIndex >= steps.length) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .logo-container svg {
            width: 100% !important;
            height: 100% !important;
            max-width: 134px;
            max-height: 134px;
          }
        `}
      </style>

      <Joyride
        callback={handleJoyrideCallback}
        continuous
        hideCloseButton
        run={shouldShowTour}
        scrollToFirstStep
        showProgress
        showSkipButton={false}
        stepIndex={stepIndex}
        steps={steps}
        styles={{
          options: {
            arrowColor: "#EDEAE4",
            backgroundColor: "#EDEAE4",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            primaryColor: "#232329",
            textColor: "#232329",
            zIndex: 1000,
          },
          tooltip: {
            borderRadius: 12,
            fontSize: 16,
            fontFamily: "inherit",
          },
          tooltipContainer: {
            direction: i18n.language === "ar" ? "rtl" : "ltr",
          },
        }}
        locale={{
          back: t("tour.back"),
          close: t("tour.close"),
          last: t("tour.finish"),
          next: t("tour.next"),
          skip: t("tour.skip"),
        }}
      />
    </>
  );
};

export default CirclesPageTour;
